/**
 * Deterministic SAMPLE day of a Teltonika-equipped electric forklift (ADR 0034): battery voltage behind a
 * 1:4 divider on AIN2, a lift (mast) signal on AIN1, ignition, speed and green-driving (eco) events. Used by
 * the simulator (tools/simulator, Teltonika mode) and the console's demo mode. Fictional sample data only;
 * nothing here describes a real vehicle.
 *
 * The curve: a 48 V lead-acid battery, work 06:00-12:00 and 12:40-17:00 (voltage sags under load, falls with
 * use), an opportunity charge in the lunch break (12:05-12:35) and a full charge in the evening (17:15-23:00,
 * rising to the gassing voltage, then back to the rest voltage once the charger stops).
 */
import { ANALOG_DEFAULTS } from "../analog.js";

export type SampleDayPoint = {
  at: number;
  ign: boolean;
  speedKmh: number;
  lat: number;
  lon: number;
  /** Battery voltage (V) as measured; ain2Mv = batV / divider × 1000. */
  batV: number;
  ain2Mv: number;
  /** Lift sensor (V): about 0.5 at rest, about 3.5 while the mast lifts; ain1Mv = liftV × 1000. */
  ain1Mv: number;
  lifting: boolean;
  /** DC-DC converter output (mV): nearly constant, never the battery voltage. */
  extMv: number;
  green?: { type: 1 | 2 | 3; value: number };
};

export const SAMPLE_DAY = Object.freeze({
  /** Divider ratio of the sample wiring: battery volts = AIN2 volts × 4. */
  divider: 4,
  nominalV: 48,
  /** Lift sensor thresholds of the sample calibration (V, scale 1, offset 0). */
  liftOn: 2.5,
  liftOff: 1.5,
  /** Sample site (fictional coordinates near Kartepe). */
  lat: 40.7346,
  lon: 30.0732,
});

/** Small seeded PRNG (mulberry32): the same seed gives the same day. */
export function seededRandom(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const H = 3600_000;

/**
 * One sample every `stepS` seconds from `dayStart` (UTC ms of local midnight) for 24 h.
 * `wear` (0..1) shifts the whole curve down a little, so sample vehicles differ.
 */
export function sampleDay(dayStart: number, opts: { seed?: number; stepS?: number; wear?: number; hours?: number } = {}): SampleDayPoint[] {
  const rnd = seededRandom(opts.seed ?? 1);
  const step = (opts.stepS ?? 30) * 1000;
  const wear = opts.wear ?? 0;
  const n = Math.floor(((opts.hours ?? 24) * H) / step);
  const out: SampleDayPoint[] = [];
  // Rest voltage (V) after the evening charge and its fall per working hour.
  let rest = 51.0 - wear * 0.6;
  let liftLeft = 0;
  let nextLift = 2 * 60_000;
  let lat = SAMPLE_DAY.lat;
  let lon = SAMPLE_DAY.lon;
  let heading = rnd() * Math.PI * 2;
  for (let i = 0; i < n; i++) {
    const at = dayStart + i * step;
    const h = (at - dayStart) / H;
    const working = (h >= 6 && h < 12) || (h >= 12 + 40 / 60 && h < 17);
    let v: number;
    let speed = 0;
    let lifting = false;
    let green: SampleDayPoint["green"];
    if (working) {
      rest -= (0.32 * step) / H; // about 3 V over a shift
      // Moving about half of the time, 3-11 km/h.
      const moving = rnd() < 0.55;
      speed = moving ? Math.round((3 + rnd() * 8) * 10) / 10 : 0;
      if (liftLeft <= 0 && at - dayStart >= nextLift) {
        liftLeft = 15_000 + Math.floor(rnd() * 25_000);
        nextLift = at - dayStart + 120_000 + Math.floor(rnd() * 180_000);
      }
      if (liftLeft > 0) {
        lifting = true;
        liftLeft -= step;
        // Usually standing while lifting; sometimes creeping or driving with the mast up.
        speed = rnd() < 0.85 ? 0 : Math.round((2 + rnd() * 5) * 10) / 10;
      }
      const sag = (speed > 0 ? 0.9 : 0.2) + (lifting ? 1.6 : 0);
      v = rest - sag + (rnd() - 0.5) * 0.15;
      if (speed > 0 && rnd() < 0.012) {
        const type = (1 + Math.floor(rnd() * 3)) as 1 | 2 | 3;
        green = { type, value: Math.round(30 + rnd() * 45) };
      }
      heading += (rnd() - 0.5) * 0.6;
      const d = (speed / 3.6) * (step / 1000);
      lat += (d * Math.cos(heading)) / 111_195;
      lon += (d * Math.sin(heading)) / (111_195 * Math.cos((lat * Math.PI) / 180));
      // Stay on the sample site (about 150 m around the centre).
      if (Math.abs(lat - SAMPLE_DAY.lat) > 0.0013 || Math.abs(lon - SAMPLE_DAY.lon) > 0.0018) heading += Math.PI;
    } else if (h >= 12 + 5 / 60 && h < 12 + 35 / 60) {
      // Opportunity charge in the break: the charger lifts the voltage to about 55.5 V (2.31 V/cell).
      const m = (h - (12 + 5 / 60)) * 60;
      v = Math.min(55.5, rest + m * 1.2) + (rnd() - 0.5) * 0.05;
      if (h >= 12 + 34 / 60) rest += 0.5;
    } else if (h >= 17.25 && h < 23) {
      // Evening charge: rises to about 57.5 V.
      const m = (h - 17.25) / 5.75;
      v = rest + (57.5 - rest) * Math.min(1, m * 1.25) + (rnd() - 0.5) * 0.05;
    } else if (h >= 23) {
      rest = 51.0 - wear * 0.6;
      v = rest + (rnd() - 0.5) * 0.05;
    } else {
      v = rest + (rnd() - 0.5) * 0.05;
    }
    v = Math.round(v * 100) / 100;
    out.push({
      at,
      ign: working,
      speedKmh: speed,
      lat: Math.round(lat * 1e7) / 1e7,
      lon: Math.round(lon * 1e7) / 1e7,
      batV: v,
      ain2Mv: Math.round((v / SAMPLE_DAY.divider) * 1000),
      ain1Mv: lifting ? 3400 + Math.floor(rnd() * 300) : 450 + Math.floor(rnd() * 150),
      lifting,
      extMv: 24_050 + Math.floor(rnd() * 120),
      ...(green ? { green } : {}),
    });
  }
  return out;
}

/** The sample calibration that matches sampleDay (for demo vehicles and simulator tests). */
export const SAMPLE_CALIBRATION = Object.freeze({
  batterySource: "ain2" as const,
  batScale: SAMPLE_DAY.divider,
  batOffset: 0,
  // Owner decision pending: the low-battery alert stays off by default.
  batLowV: null as number | null,
  liftSource: "ain1" as const,
  liftScale: 1,
  liftOffset: 0,
  liftOn: SAMPLE_DAY.liftOn,
  liftOff: SAMPLE_DAY.liftOff,
  liftDriveKmh: ANALOG_DEFAULTS.liftDriveKmh,
  chargeRiseV: ANALOG_DEFAULTS.chargeRiseV,
  chargeV: null as number | null,
});
